


import { useEffect, useState } from "react";
import { DashboardAPI } from "../../../services/api/dashboard";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    CircularProgress,
    Paper,
    Divider,
    useTheme,
    Container
} from "@mui/material";
import {
    CheckCircle as ApprovedIcon,
    Cancel as RejectedIcon,
    Pending as PendingIcon,
    Lock as ClosedIcon,
    Category as CategoryIcon,
    ShoppingBasket as ProductsIcon,
    Layers as SubcategoriesIcon,
    Assignment as EnquiriesIcon
} from "@mui/icons-material";
import { Grow } from "@mui/material";

const StatCard = ({ icon, title, value, index }) => {
    return (
        <Grow in={true} timeout={index * 200}>
            <Card sx={{
                height: '90%',
                borderRadius: '12px',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px 0 rgba(0,0,0,0.12)'
                }
            }}>
                <CardContent sx={{ p: 1, pb: 0 }}>
                    <Box display="flex" alignItems="center">
                        <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2
                        }}>
                            {icon}
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                {title}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                {value}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Grow>
    );
};

const StatusCard = ({ status, count }) => {
    const theme = useTheme();

    const statusColors = {
        APPROVED: theme.palette.success.light,
        REJECTED: theme.palette.error.light,
        PENDING: theme.palette.warning.light,
        CLOSED: theme.palette.grey[300]
    };

    const statusIcons = {
        APPROVED: <ApprovedIcon />,
        REJECTED: <RejectedIcon />,
        PENDING: <PendingIcon />,
        CLOSED: <ClosedIcon />
    };

    return (
        <Grow in={true}>
            <Paper sx={{
                p: 2.5,
                borderRadius: '12px',
                backgroundColor: statusColors[status],
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                    transform: 'scale(1.02)'
                }
            }}>
                <Box display="flex" alignItems="center">
                    <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                    }}>
                        {statusIcons[status]}
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                            {status}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                            {count}
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Grow>
    );
};

export function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const subdomain = localStorage.getItem("vendorsubdomain");

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await DashboardAPI(subdomain);
            if (response.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!data) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <Typography variant="h6">Failed to load dashboard data</Typography>
            </Box>
        );
    }

    return (
        <Box >
            <Container>
                <Typography variant="h5" gutterBottom sx={{ my: 3, fontWeight: 500 }}>
                    Dashboard Overview
                </Typography>

                {/* Summary Cards */}
                <Grid container spacing={1}>
                    {[
                        {
                            icon: <EnquiriesIcon color="action" fontSize="medium" />,
                            title: "Total Enquiries",
                            value: data.totalEnquiries
                        },
                        {
                            icon: <ProductsIcon color="action" fontSize="medium" />,
                            title: "Total Products",
                            value: data.totalProducts
                        },
                        {
                            icon: <CategoryIcon color="action" fontSize="medium" />,
                            title: "Total Categories",
                            value: data.totalCategories
                        },
                        {
                            icon: <SubcategoriesIcon color="action" fontSize="medium" />,
                            title: "Total Subcategories",
                            value: data.totalSubcategories
                        },
                    ].map((item, index) => (
                        <Grid item sx={12} md={3} key={index}>
                            <StatCard {...item} index={index} />
                        </Grid>
                    ))}
                </Grid>
            </Container>




        </Box>
    );
}